import { useQuery } from "@tanstack/react-query";
import type { SharedAdminFailureCircuit } from "../../../../../../shared/frontend/admin/adminTypes";
import { useAuth } from "../auth/useAuth";
import { useRealtimeListRefresh } from "../realtime/RealtimeContext";
import { RealtimeEventTypes } from "../realtime/realtimeEventTypes";
import { PLATFORM_ADMIN_ROLE } from "../constants/authRole";

/** Rechte, mit denen der Mandant seine Anbindungen sieht (wie im Backend `ConnectionAttentionController`). */
const connectionPermissions = ["integrations.read", "integrations.write"] as const;

/** Schlüssel der Abfrage; die Integrationsseite teilt sich Cache und Abruf mit dem Menühinweis. */
export function connectionAttentionQueryKey(accountId: string | null | undefined, tenantId: string | null | undefined) {
  return ["connection-attention", accountId, tenantId] as const;
}

/**
 * Zahl der angehaltenen Anbindungen des Mandanten für die Menümarkierung. Nur mit Recht auf
 * Integrationen; für Plattform-Admins gibt es keine Mandantenansicht im Menü. Ein Fehler lässt den
 * Hinweis still weg, die Integrationsseite bleibt maßgeblich.
 */
export function useConnectionAttentionCount(): number {
  const auth = useAuth();
  const enabled = !!auth.info && auth.info.role !== PLATFORM_ADMIN_ROLE && auth.hasAnyPermission(...connectionPermissions);
  const query = useQuery({
    queryKey: connectionAttentionQueryKey(auth.info?.accountId, auth.info?.tenantId),
    enabled,
    queryFn: async () => await auth.api.get<SharedAdminFailureCircuit[]>("/api/v1/connection-attention", { suppressGlobalErrorToast: true }) ?? [],
    retry: false,
  });
  const reload = query.refetch;
  useRealtimeListRefresh({
    eventTypes: [RealtimeEventTypes.ConnectionAttentionChanged],
    reload: () => { void reload(); },
    enabled,
  });
  return enabled ? query.data?.length ?? 0 : 0;
}
